import {LangKeyType} from '../../../../../../../provider/locale/translation/type';

import {
    AddressCommentSegmentKindEnum,
    AddressCommentSegmentType,
    AddressCommentTemplateFieldType,
    AddressCommentTemplateType,
} from './address-comment-templates-type';

type GetLocalizedStringType = (stringKey: LangKeyType, valueMap?: Record<string, string>) => string;

/**
 * A part of the pattern that stands or falls together: the field and the words that introduce it.
 * Dropping the whole group is what keeps "Внутри ТЦ Максимум, офис 312" from becoming
 * "Внутри ТЦ Максимум, этаж , офис 312" when the floor is left empty.
 */
type GroupType = {
    field: AddressCommentTemplateFieldType | null;
    segmentList: Array<AddressCommentSegmentType>;
};

const placeholderRegExp = /{(\w+)}/g;
const groupRegExp = /\[([^[\]]*)]/g;

function parseSegmentList(groupText: string, template: AddressCommentTemplateType): Array<AddressCommentSegmentType> {
    return groupText
        .split(placeholderRegExp)
        .map((part, index): AddressCommentSegmentType | null => {
            // String.split with a capturing group alternates static text and placeholder names.
            if (index % 2 === 0) {
                // Whitespace-only text is kept: it separates two placeholders standing next to each other.
                const text = part.replace(/\s+/gu, ' ');

                return text ? {kind: AddressCommentSegmentKindEnum.text, text} : null;
            }

            const field = template.fieldList.find(({name}) => name === part);

            return field ? {kind: AddressCommentSegmentKindEnum.field, field} : null;
        })
        .filter((segment): segment is AddressCommentSegmentType => segment !== null);
}

function parseGroupList(
    template: AddressCommentTemplateType,
    getLocalizedString: GetLocalizedStringType
): Array<GroupType> {
    const pattern = getLocalizedString(template.patternKey);
    const groupTextList = pattern.includes('[')
        ? [...pattern.matchAll(groupRegExp)].map(([, groupText]) => groupText ?? '')
        : // a pattern without optional groups is a single mandatory one
          [pattern];

    return groupTextList.map((groupText) => {
        const segmentList = parseSegmentList(groupText, template);
        const fieldSegment = segmentList.find((segment) => segment.kind === AddressCommentSegmentKindEnum.field);

        return {
            field: fieldSegment?.kind === AddressCommentSegmentKindEnum.field ? fieldSegment.field : null,
            segmentList,
        };
    });
}

/**
 * The segments to render, both as a tagged sentence in the template list and as a row of inputs.
 * Every group is rendered — a field is only dropped when the comment is assembled, never from the row.
 * Whitespace-only text is dropped here: in a flex row it would claim a gap of its own.
 */
export function getAddressCommentSegmentList(
    template: AddressCommentTemplateType,
    getLocalizedString: GetLocalizedStringType
): Array<AddressCommentSegmentType> {
    return parseGroupList(template, getLocalizedString)
        .flatMap(({segmentList}) => segmentList)
        .filter((segment) => segment.kind !== AddressCommentSegmentKindEnum.text || segment.text.trim());
}

/**
 * Builds the address comment out of the values the user typed into the template.
 * Groups whose field is empty are dropped, so a partly filled template still yields a readable
 * comment. Returns an empty string while no field is filled at all — an untouched template
 * must not overwrite the comment the company already has.
 */
export function buildAddressComment(
    template: AddressCommentTemplateType,
    fieldValueMap: Record<string, string>,
    getLocalizedString: GetLocalizedStringType
): string {
    const filledGroupList = parseGroupList(template, getLocalizedString).filter(
        ({field}) => !field || fieldValueMap[field.name]?.trim()
    );

    if (template.fieldList.length > 0 && !filledGroupList.some(({field}) => field)) {
        return '';
    }

    const comment = filledGroupList
        .flatMap(({segmentList}) => segmentList)
        .map((segment) =>
            segment.kind === AddressCommentSegmentKindEnum.text
                ? segment.text
                : fieldValueMap[segment.field.name]?.trim() ?? ''
        )
        .join('')
        .replace(/\s+/gu, ' ')
        .trim()
        // a dropped leading group can leave the separator that introduced the next one
        .replace(/^[,;]+\s*/u, '')
        .replace(/\s*[,;]+$/u, '')
        .trim();

    return comment.includes('{') || comment.includes('}') ? '' : comment;
}
